import type React from 'react';
import { range } from 'lodash-es';

import { Box, Divider, FormField, Input, Label, Paragraph, Text, TextButton } from '@zillow/constellation-10';
import { IconPlusFilled } from '@zillow/constellation-icons';

import { SNOW_REMOVAL } from '../../../../constants';
import type { Section } from '../../../../models';
import { Field, SnowRemovalSection } from '../../../../models';
import HeaderToggle from '../Utilities/HeaderToggle';
import AreaButtonGroup_v2 from '../Utilities/AreaButtonGroup_v2';
import { useIndexedSection } from '../../../../hooks';

export const MAXIMUM_CUSTOM_AREAS = 4;

type Props = {
  section: Section;
  setSectionValues: (object) => void;
  setSection: (section: SnowRemovalSection) => void;
};

interface Area {
  fieldName: string;
  areaName: string;
}

export default function SnowRemoval_v2({ section, setSectionValues, setSection }: Props): JSX.Element {
  const { onAddOne: addOneIndex, onDeleteOne: onDeleteArea } = useIndexedSection({
    section,
    setSection,
    SectionClass: SnowRemovalSection,
  });

  function toggleUtility(): void {
    const isEnabled = section.getFieldValue(SNOW_REMOVAL.FIELD_NAME.IS_ENABLED);

    setSectionValues({
      [SNOW_REMOVAL.FIELD_NAME.IS_ENABLED]: !isEnabled,
    });
  }

  function setButtonGroupClick(fieldName: string, buttonValue: string): void {
    setSectionValues({ [fieldName]: buttonValue });
  }

  function setCustomDescription(e: React.ChangeEvent<HTMLInputElement>): void {
    const { index } = e.currentTarget.dataset;
    const fieldName = Field.getIndexedFieldName(SNOW_REMOVAL.FIELD_NAME.CUSTOM_AREA_DESCRIPTION, Number(index));

    setSectionValues({ [fieldName]: e.currentTarget.value });
  }

  function renderDefaultAreas(): React.ReactNode {
    const areas: Array<Area> = [
      {
        fieldName: SNOW_REMOVAL.FIELD_NAME.COMMON_SIDEWALK,
        areaName: SNOW_REMOVAL.LABEL.COMMON_SIDEWALK,
      },
      {
        fieldName: SNOW_REMOVAL.FIELD_NAME.MAIN_EXTERIOR_STAIRS,
        areaName: SNOW_REMOVAL.LABEL.MAIN_EXTERIOR_STAIRS,
      },
      {
        fieldName: SNOW_REMOVAL.FIELD_NAME.SECONDARY_STAIRS,
        areaName: SNOW_REMOVAL.LABEL.SECONDARY_STAIRS,
      },
      {
        fieldName: SNOW_REMOVAL.FIELD_NAME.COMMON_DRIVEWAY,
        areaName: SNOW_REMOVAL.LABEL.COMMON_DRIVEWAY,
      },
      {
        fieldName: SNOW_REMOVAL.FIELD_NAME.DRIVE_LANES,
        areaName: SNOW_REMOVAL.LABEL.DRIVE_LANES,
      },
      {
        fieldName: SNOW_REMOVAL.FIELD_NAME.PARKING_SPACES,
        areaName: SNOW_REMOVAL.LABEL.PARKING_SPACES,
      },
    ];

    return areas.map((area: Area, index: number) => (
      <AreaButtonGroup_v2
        setButtonGroupClick={setButtonGroupClick}
        areaName={area.areaName}
        fieldName={area.fieldName}
        buttonValue={section.getFieldValue(area.fieldName)}
        key={index}
      />
    ));
  }

  function renderCustomAreas(): React.ReactNode {
    const totalCustomAreas = section.getTotalIndexes();

    return range(totalCustomAreas).map((index: number) => {
      const descriptionFieldName = Field.getIndexedFieldName(SNOW_REMOVAL.FIELD_NAME.CUSTOM_AREA_DESCRIPTION, index);
      const responsibilityFieldName = Field.getIndexedFieldName(
        SNOW_REMOVAL.FIELD_NAME.CUSTOM_AREA_RESPONSIBILITY,
        index,
      );
      const responsibilityValue = section.getFieldValue(responsibilityFieldName);
      const descriptionValue = section.getFieldValue(descriptionFieldName);

      return (
        <div key={index}>
          <Divider css={{ marginTop: 'layout.tighter', marginBottom: 'layout.tighter' }} />
          <Box key={index}>
            <Box css={{ display: 'grid', gridTemplateColumns: '2fr 1fr' }}>
              <Box>
                <Text textStyle="body-bold">Additional area #{index + 1}</Text>
              </Box>
              <Box css={{ display: 'flex', justifyContent: 'flex-end' }}>
                <TextButton
                  aria-label="Delete area"
                  tabIndex={0}
                  role="button"
                  data-test="delete-link"
                  data-index={index}
                  onClick={onDeleteArea}
                  onKeyUp={onKeyUp}
                >
                  Delete
                </TextButton>
              </Box>
            </Box>
            <FormField
              required
              label={<Label id="description">Describe this area</Label>}
              control={
                <Input
                  aria-labelledby="description"
                  data-index={index}
                  data-test="input-description"
                  value={descriptionValue}
                  onChange={setCustomDescription}
                  css={{ marginBottom: 'layout.tight' }}
                />
              }
            />
            <AreaButtonGroup_v2
              setButtonGroupClick={setButtonGroupClick}
              fieldName={responsibilityFieldName}
              buttonValue={responsibilityValue}
              twoButtonArea
            />
          </Box>
        </div>
      );
    });
  }

  function onKeyUp(event): void {
    if (event.keyCode === 13) {
      event.target.click();
    }
  }

  const snowRemoval_isEnabled = section.getFieldValue(SNOW_REMOVAL.FIELD_NAME.IS_ENABLED);
  const totalIndex = section.getTotalIndexes();

  if (totalIndex == null) {
    return null;
  }

  return (
    <>
      <HeaderToggle
        header="Snow Removal"
        subheader="Applies to this property"
        switchOn={snowRemoval_isEnabled}
        onSwitchToggle={toggleUtility}
      />
      {snowRemoval_isEnabled && (
        <>
          <Divider css={{ marginTop: 'layout.tighter', marginBottom: 'layout.tighter' }} />
          <Paragraph css={{ marginBottom: 'layout.tight' }}>
            Who will be responsible for snow removal in the following areas?
          </Paragraph>
          {renderDefaultAreas()}
          {renderCustomAreas()}
          <Box css={{ marginTop: 'layout.tight' }}>
            {section.getTotalIndexes() === MAXIMUM_CUSTOM_AREAS ? (
              <Text>
                <span data-test="maximum-reached">
                  You’ve reached {MAXIMUM_CUSTOM_AREAS} additional areas, which is the maximum allowable amount.
                </span>
              </Text>
            ) : (
              <TextButton
                aria-label="Add another area"
                tabIndex={0}
                role="button"
                data-test="add-another"
                onClick={addOneIndex}
                onKeyUp={onKeyUp}
                icon={<IconPlusFilled />}
              >
                Add another area
              </TextButton>
            )}
          </Box>
        </>
      )}
    </>
  );
}

SnowRemoval_v2.description = 'Remove N/A from selection for additional areas';
